import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@data-delivery/d3s';
import { productsQueries } from '@data-delivery/permission-ui-data-access-state';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

export interface AssignmentProductsCsvDropzoneProps {
  products?: string[];
  businessGroupId?: string;
  isEditMode?: boolean;
  className?: string;
  onBulkAddProducts?: (productIds: string[]) => void;
  onBulkRemoveProducts?: (productIds: string[]) => void;
}

async function readCsvTokens(file: File): Promise<string[]> {
  const text = await file.text();
  return text
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean);
}

function normalize(val: string | undefined): string {
  return (val || '').trim().toLowerCase();
}

export function matchTokensToProductIds(
  tokens: string[],
  productsPool: Array<{ id: string; name?: string; crmCode?: string; crmGroup?: string }>,
): string[] {
  if (!tokens.length || !productsPool.length) {
    return [];
  }

  const byName = new Map<string, string[]>();
  const byCode = new Map<string, string[]>();
  const byGroup = new Map<string, string[]>();

  for (const p of productsPool) {
    const nameKey = normalize(p.name);
    const nameStripped = (() => {
      if (!p.name) {
        return '';
      }

      const idx = p.name.indexOf(':');
      if (idx === -1) {
        return '';
      }

      return normalize(p.name.slice(idx + 1));
    })();
    const codeKey = normalize(p.crmCode);
    const groupKey = normalize(p.crmGroup);

    if (nameKey) {
      const arr = byName.get(nameKey) ?? [];
      arr.push(p.id);
      byName.set(nameKey, arr);
    }

    if (nameStripped) {
      const arr = byName.get(nameStripped) ?? [];
      arr.push(p.id);
      byName.set(nameStripped, arr);
    }

    if (codeKey) {
      const arr = byCode.get(codeKey) ?? [];
      arr.push(p.id);
      byCode.set(codeKey, arr);
    }

    if (groupKey) {
      const arr = byGroup.get(groupKey) ?? [];
      arr.push(p.id);
      byGroup.set(groupKey, arr);
    }
  }

  const result = new Set<string>();
  for (const t of tokens) {
    const key = normalize(t);
    const keyStripped = (() => {
      const idx = t.indexOf(':');
      if (idx === -1) {
        return '';
      }

      return normalize(t.slice(idx + 1));
    })();
    const nameHits = [
      ...(byName.get(key) ?? []),
      ...(keyStripped ? byName.get(keyStripped) ?? [] : []),
    ];
    const codeHits = byCode.get(key) ?? [];
    const groupHits = byGroup.get(key) ?? [];
    for (const id of [...nameHits, ...codeHits, ...groupHits]) {
      result.add(id);
    }
  }

  return Array.from(result);
}

export const AssignmentProductsCsvDropzone: React.FC<AssignmentProductsCsvDropzoneProps> = ({
  products,
  businessGroupId,
  isEditMode,
  className,
  onBulkAddProducts,
  onBulkRemoveProducts,
}) => {
  const { data: allProducts = [], isLoading } = useQuery(productsQueries.list());

  const pool = businessGroupId
    ? allProducts.filter(p => p.businessGroup === businessGroupId)
    : allProducts;

  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [open, setOpen] = useState(false);
  const modeOptions = useMemo(
    () => [
      { value: 'add', label: 'Add products' },
      { value: 'remove', label: 'Remove products' },
    ],
    [],
  );

  async function handleBulkAdd(file: File) {
    const tokens = await readCsvTokens(file);
    const matched = matchTokensToProductIds(tokens, pool);
    const newOnly = matched.filter(id => !(products ?? []).includes(id));
    onBulkAddProducts?.(newOnly);
  }

  async function handleBulkRemove(file: File) {
    const tokens = await readCsvTokens(file);
    const matched = matchTokensToProductIds(tokens, pool);
    const existingOnly = matched.filter(id => (products ?? []).includes(id));
    onBulkRemoveProducts?.(existingOnly);
  }

  return (
    <div className={className}>
      <div className='flex items-center gap-2 mb-2'>
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <button type='button' className='rounded-md border px-3 py-2 text-xs hover:bg-muted'>
              {open ? 'Hide bulk CSV' : 'Show bulk CSV'}
            </button>
          </CollapsibleTrigger>
        </Collapsible>
      </div>

      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleContent>
          <div className='flex flex-col gap-3 w-full'>
            <div className='text-xs text-muted-foreground'>
              Use this area to bulk manage products via a CSV file. Choose whether to{' '}
              <span className='font-medium'>add</span> new products to the selection or{' '}
              <span className='font-medium'>remove</span> products already selected, then upload a
              CSV or drag-and-drop it here. Product names may include a business group prefix like{' '}
              <code>BG:PRODUCT_NAME</code>; matching works with or without the prefix.
              <br />
              <br />
              CSV format: one token per line. Tokens can be{' '}
              <span className='font-medium'>Product Name</span> (with or without BG prefix),{' '}
              <span className='font-medium'>CRM Code</span>, or{' '}
              <span className='font-medium'>CRM Group</span>.
            </div>
            <div className='flex items-center gap-2'>
              <Select
                value={mode}
                onValueChange={val => {
                  setMode((val as 'add' | 'remove') ?? 'add');
                }}
              >
                <SelectTrigger className='w-[220px]'>
                  <SelectValue placeholder='Choose action' />
                </SelectTrigger>
                <SelectContent>
                  {modeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex items-stretch gap-2 w-full'>
              <Dropzone
                accept={{ 'text/csv': ['.csv'], 'text/plain': ['.txt'] }}
                maxFiles={1}
                disabled={
                  isLoading ||
                  (mode === 'remove' && (!isEditMode || !products || products.length === 0))
                }
                onDrop={async accepted => {
                  const f = accepted?.[0];
                  if (!f) {
                    return;
                  }

                  if (mode === 'remove') {
                    await handleBulkRemove(f);
                  } else {
                    await handleBulkAdd(f);
                  }
                }}
                className='flex-1'
              >
                <DropzoneEmptyState />
                <DropzoneContent />
              </Dropzone>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default AssignmentProductsCsvDropzone;
